import { useEffect, useState } from "react";
import { api, fmtTime, type Customer, type CustomerStage, type CustomerWebapp } from "../api";

// Onboarding funnel, in the order a customer moves through it.
const STAGES: { key: CustomerStage; label: string; hint: string; tone: "" | "ok" | "bad" }[] = [
  {
    key: "email_unverified",
    label: "email not verified",
    hint: "Signed up but never clicked the verification mail, so they cannot log in.",
    tone: "bad",
  },
  {
    key: "no_webapp",
    label: "no webapp",
    hint: "Logged in but never created a webapp.",
    tone: "bad",
  },
  {
    key: "not_integrated",
    label: "never called API",
    hint: "Has a webapp but no OTP was ever issued with its API key.",
    tone: "bad",
  },
  {
    key: "no_verification",
    label: "no verification yet",
    hint: "Issues OTPs through /auth/init, but no end-user ever completed one.",
    tone: "bad",
  },
  { key: "active", label: "active", hint: "At least one end-user verification.", tone: "ok" },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map((st) => [st.key, st]));

function stageDetail(cu: Customer): string {
  const f = cu.funnel;
  switch (cu.stage) {
    case "email_unverified":
      return `${f.verify_mails} verify mail${f.verify_mails === 1 ? "" : "s"} sent` +
        (f.last_verify_mail_at ? `, last ${fmtTime(f.last_verify_mail_at)}` : "");
    case "no_webapp":
      return cu.last_login_at ? `last login ${fmtTime(cu.last_login_at)}` : "never logged in";
    case "not_integrated":
      return `${f.webapps_ever} webapp${f.webapps_ever === 1 ? "" : "s"}, first ${fmtTime(f.first_webapp_at)}`;
    case "no_verification":
      return `${f.otp_issued} OTP issued, 0 verified` + (f.last_otp_day ? `, last ${f.last_otp_day}` : "");
    case "active":
      return `${f.otp_verified} verified` + (f.last_otp_day ? `, last ${f.last_otp_day}` : "");
  }
}

export function CustomersTab() {
  const [items, setItems] = useState<Customer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [openWebapps, setOpenWebapps] = useState<Customer | null>(null);
  const [stageFilter, setStageFilter] = useState<CustomerStage | null>(null);
  const [suspending, setSuspending] = useState<Customer | null>(null);

  async function reload() {
    try {
      const r = await api.listCustomers();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load_failed");
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function reinstate(cu: Customer) {
    if (!confirm(`Lift the suspension on ${cu.email}? Their webapps start answering again at once.`)) return;
    try {
      await api.reinstateCustomer(cu.id);
      reload();
    } catch (e) {
      alert(e instanceof Error ? e.message : "reinstate_failed");
    }
  }

  async function remove(cu: Customer) {
    if (!confirm(`Delete customer ${cu.email}? This cannot be undone.`)) return;
    try {
      await api.deleteCustomer(cu.id);
      reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "delete_failed";
      alert(msg === "has_webapps" ? "Customer still owns webapps — delete those first." : msg);
    }
  }

  return (
    <>
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Customers</h2>
        </div>
        {err && <div className="error">{err}</div>}
        {items && items.length > 0 && (
          <>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", margin: "8px 0" }}>
              {STAGES.map((st) => {
                const n = items.filter((cu) => cu.stage === st.key).length;
                const on = stageFilter === st.key;
                return (
                  <button
                    key={st.key}
                    type="button"
                    className={on ? "" : "secondary"}
                    title={st.hint}
                    style={{ flex: "0 0 auto" }}
                    onClick={() => setStageFilter(on ? null : st.key)}
                  >
                    {st.label}: {n}
                  </button>
                );
              })}
            </div>
            <p className="muted" style={{ marginTop: 0 }}>
              OTPs that lapsed without the webapp polling them were not counted before this
              release, so some "never called API" accounts may in fact have tried.
            </p>
          </>
        )}
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No customers yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Stage</th>
                  <th>Phone</th>
                  <th>Webapps</th>
                  <th>Credits</th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter((cu) => !stageFilter || cu.stage === stageFilter)
                  .map((cu) => {
                    const low = cu.credits_remaining <= 0;
                    const st = STAGE_BY_KEY[cu.stage];
                    return (
                      <tr key={cu.id}>
                        <td data-label="Email">
                          {cu.email}
                          {!cu.email_verified && (
                            <>
                              {" "}
                              <span className="pill bad">unverified</span>
                            </>
                          )}
                          <br />
                          {cu.agreement_accepted_at ? (
                            <span
                              className="muted"
                              title={`User agreement ${cu.agreement_version} accepted`}
                            >
                              agreement ✓ {fmtTime(cu.agreement_accepted_at)}
                            </span>
                          ) : (
                            <span
                              className="pill bad"
                              title={`Has not accepted user agreement ${cu.agreement_version}; portal is blocked until they do`}
                            >
                              agreement not accepted
                            </span>
                          )}
                          {cu.suspended_at && (
                            <>
                              <br />
                              <span
                                className="pill bad"
                                title={`Suspended ${fmtTime(cu.suspended_at)}${cu.suspended_by ? ` by ${cu.suspended_by}` : ""}`}
                              >
                                suspended
                              </span>{" "}
                              <span className="muted">{cu.suspended_reason}</span>
                            </>
                          )}
                        </td>
                        <td data-label="Stage">
                          <span className={`pill ${st?.tone ?? ""}`} title={st?.hint}>
                            {st?.label ?? cu.stage}
                          </span>
                          <br />
                          <span className="muted">{stageDetail(cu)}</span>
                        </td>
                        <td data-label="Phone">
                          {cu.phone ? <code>{cu.phone}</code> : <span className="muted">—</span>}
                        </td>
                        <td data-label="Webapps">{cu.webapp_count}</td>
                        <td data-label="Credits">
                          <code style={{ whiteSpace: "nowrap" }}>
                            {cu.consumed_credits} / {cu.credit_allowance}
                          </code>
                          <br />
                          <span className={`pill ${low ? "bad" : "ok"}`}>
                            {cu.credits_remaining} left
                          </span>
                        </td>
                        <td data-label="Joined">{fmtTime(cu.created_at)}</td>
                        <td data-label="Last login">{fmtTime(cu.last_login_at)}</td>
                        <td className="actions" data-label="Actions">
                          <div className="action-group">
                            <button className="secondary" onClick={() => setOpenWebapps(cu)}>
                              Webapps
                            </button>
                            <button className="secondary" onClick={() => setEditing(cu)}>
                              Credits
                            </button>
                            {cu.suspended_at ? (
                              <button className="secondary" onClick={() => reinstate(cu)}>
                                Reinstate
                              </button>
                            ) : (
                              <button className="danger" onClick={() => setSuspending(cu)}>
                                Suspend
                              </button>
                            )}
                            <button className="danger" onClick={() => remove(cu)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditCustomerModal
          customer={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {suspending && (
        <SuspendCustomerModal
          customer={suspending}
          onCancel={() => setSuspending(null)}
          onDone={() => {
            setSuspending(null);
            reload();
          }}
        />
      )}

      {openWebapps && (
        <CustomerWebappsModal customer={openWebapps} onClose={() => setOpenWebapps(null)} />
      )}
    </>
  );
}

function SuspendCustomerModal({
  customer,
  onCancel,
  onDone,
}: {
  customer: Customer;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.suspendCustomer(customer.id, reason.trim());
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "suspend_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Suspend — {customer.email}</h3>
        <p className="muted">
          Takes effect immediately and only an admin can lift it. Every webapp of this account gets
          403 account_suspended from the API, in-flight OTPs are failed, and the portal shows a
          suspension notice: no webapp changes, no credit purchases, no self-service account
          deletion. Data export and logout stay available.
        </p>
        <label>
          Reason <span className="muted">(internal, not shown to the customer)</span>
        </label>
        <textarea
          rows={3}
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. used on an illegal betting site (example.com), §4"
          style={{ width: "100%" }}
        />
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onCancel}>
              Cancel
            </button>{" "}
            <button type="submit" className="danger" disabled={busy || !reason.trim()}>
              {busy ? "Suspending…" : "Suspend account"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function EditCustomerModal({
  customer,
  onCancel,
  onSaved,
}: {
  customer: Customer;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [trial, setTrial] = useState(String(customer.trial_credits));
  const [paid, setPaid] = useState(String(customer.paid_credits));
  const [verified, setVerified] = useState(customer.email_verified);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.updateCustomer(customer.id, {
        trial_credits: Number(trial) || 0,
        paid_credits: Number(paid) || 0,
        email_verified: verified,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Credits — {customer.email}</h3>
        <p className="muted">
          Spent {customer.consumed_credits} of {customer.credit_allowance} granted
          ({customer.credits_remaining} remaining). One verified login = one credit.
        </p>
        <label>
          Trial credits <span className="muted">(one-time free allowance)</span>
        </label>
        <input type="number" min={0} value={trial} onChange={(e) => setTrial(e.target.value)} />
        <label>
          Paid credits <span className="muted">(purchased + manual top-ups)</span>
        </label>
        <input type="number" min={0} value={paid} onChange={(e) => setPaid(e.target.value)} />
        <label className="row" style={{ gap: 8, alignItems: "center", marginTop: 14 }}>
          <input
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span>Email verified</span>
        </label>
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onCancel}>
              Cancel
            </button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function CustomerWebappsModal({
  customer,
  onClose,
}: {
  customer: Customer;
  onClose: () => void;
}) {
  const [items, setItems] = useState<CustomerWebapp[] | null>(null);
  useEffect(() => {
    api.customerWebapps(customer.id).then((r) => setItems(r.items));
  }, [customer.id]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ width: "min(760px, 96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{customer.email} — webapps</h3>
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No webapps.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id}>
                    <td data-label="Code">
                      <code>{w.code}</code>
                    </td>
                    <td data-label="Name">{w.name}</td>
                    <td data-label="Status">
                      {w.deleted ? (
                        <span className="pill bad">deleted</span>
                      ) : (
                        <span className={`pill ${w.active ? "ok" : "bad"}`}>
                          {w.active ? "active" : "disabled"}
                        </span>
                      )}
                    </td>
                    <td data-label="Verified">
                      <code>
                        {w.verified_count}
                        {w.unlimited ? " / ∞" : ""}
                      </code>
                    </td>
                    <td data-label="Created">{fmtTime(w.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
